import React, { useRef } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { InputAdornment, IconButton, Box } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import './CustomDatePicker.css';

// Extend Material UI theme to include 'MuiPickersDay' and 'MuiPickersToolbar'
declare module '@mui/material/styles' {
  interface Components {
    MuiPickersDay?: any;
    MuiPickersToolbar?: any;
  }
}

// Define the theme with dark mode and component customizations
const theme = createTheme({
  components: {
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: '40px',
          backgroundColor: '#2C2F33',
          color: 'white !important',
          border: '1px solid #365F91',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: 'white !important',
          backgroundColor: '#2C2F33',
          border: '1.4px solid transparent',
          background: `
            linear-gradient(black, black) padding-box,
            linear-gradient(180deg, #365f91 -4.29%, rgba(54, 95, 145, 0) 100%) border-box
          `,
          borderRadius: '40px',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: 'white',
          backgroundColor: '#2C2F33',
          '&.Mui-selected': {
            backgroundColor: '#365F91',
            color: 'white',
          },
          '&.Mui-disabled': {
            color: 'white',
          },
        },
      },
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: '#365F91',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: 'white',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: 'white !important',
        },
        caption: {
          color: 'white !important',
        },
        h6: {
          color: 'white !important',
        },
      },
    },
  },
});

interface CustomDatePickerProps {
  label: string;
  selectedDate: Date | null | undefined;
  onDateChange: (date: Date) => void;
  calendarIcon: string;
}

interface FloatingLabelInputProps {
  label: string;
  children: React.ReactNode;
  focused: boolean;
  hasValue: boolean;
  hasInput: boolean;
}

const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  label,
  children,
  focused,
  hasValue,
  hasInput,
}) => {
  return (
    <Box
      className={`floating-label-input ${focused || hasValue || hasInput ? 'focused' : ''}`}
      sx={{ position: 'relative', width: '100%', height: '60px' }}
    >
      <label
        className="floating-label"
        style={{
          position: 'absolute',
          top: focused || hasValue || hasInput ? '8px' : '50%',
          left: '16px',
          transform:
            focused || hasValue || hasInput
              ? 'translateY(-100%) scale(0.75)'
              : 'translateY(-50%) scale(1)',
          transformOrigin: 'left center',
          transition: 'all 0.2s ease',
          color: focused || hasValue || hasInput ? '#365F91' : '#fff',
          pointerEvents: 'none',
          zIndex: 1,
          background:
            focused || hasValue || hasInput ? '#2C2F33' : 'transparent', // Ensure background is dark when label floats
          padding: '0 4px', // Padding to fit background behind the label text
        }}
      >
        {label}
      </label>
      {children}
    </Box>
  );
};

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  label,
  selectedDate,
  onDateChange,
  calendarIcon,
}) => {
  const datePickerRef = useRef<HTMLButtonElement | null>(null);
  const [focused, setFocused] = React.useState(false);
  const hasValue = !!selectedDate;

  return (
    <ThemeProvider theme={theme}>
      <Box>
        <FloatingLabelInput
          label={label}
          focused={focused}
          hasValue={hasValue}
          hasInput={true}
        >
          <DatePicker
            value={selectedDate ? dayjs(selectedDate) : null}
            onChange={(newValue: Dayjs | null) => {
              onDateChange(newValue?.toDate() || new Date());
            }}
            onClose={() => setFocused(false)} // Reset focus state when DatePicker closes
            onOpen={() => setFocused(true)} // Set focus when DatePicker opens
            slots={{
              openPickerIcon: () => <></>, // Hide the default calendar icon
            }}
            slotProps={{
              textField: {
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        edge="start"
                        onClick={() => {
                          if (datePickerRef.current) {
                            datePickerRef.current.click();
                          }
                        }}
                      >
                        <img
                          src={calendarIcon}
                          alt="Calendar"
                          style={{
                            width: '24px',
                            height: '24px',
                            color: 'white',
                          }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                InputLabelProps: {
                  shrink: true, // Force the label to shrink when focused or filled
                  style: { color: 'white' }, // Set the label text color to white
                },
                // placeholder: label, // Set the placeholder to "From"
                sx: {
                  position: 'relative',
                  padding: '8px',
                  borderRadius: '40px',
                  border: '2px solid transparent',
                  backgroundImage: `
                    linear-gradient(#2C2F33, #2C2F33),
                    linear-gradient(180deg, #365f91 -4.29%, rgba(54, 95, 145, 0) 100%)
                  `,
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& input': {
                    color: 'white',
                    backgroundColor: 'transparent',
                    padding: '10px 12px',
                  },
                  '& .MuiInputBase-root': {
                    backgroundColor: 'transparent',
                  },
                },
              },
              openPickerButton: {
                ref: datePickerRef,
              },
            }}
          />
        </FloatingLabelInput>
      </Box>
    </ThemeProvider>
  );
};

export default CustomDatePicker;
